import { Button, Card, ErrorSummary } from '@beautyburst/ui';
import { semanticColor, space, typeStyle } from '@beautyburst/tokens';
import { customerHref } from '../base-path.ts';
import { useState } from 'react';
import type { FormEvent } from 'react';
import type { CustomerApiClient } from '../api-client.ts';
import {
  confirmCustomerPasswordReset,
  requestCustomerPasswordReset,
} from '../auth-verification-client.ts';
import {
  errorsForSummary,
  FormField,
  useFormErrors,
  validateEmail,
  validatePassword,
} from '../forms.tsx';

export const PASSWORD_RESET_CONFIRMATION =
  "If an account exists for that email, we've sent reset instructions.";
export const PASSWORD_RESET_FAILURE =
  'That reset link is not valid or has expired. Request a new one.';

export interface ForgotPasswordScreenProps {
  /** Kept for host compatibility while account-lifecycle calls use the dedicated auth transport. */
  client?: { readonly auth: Pick<CustomerApiClient['auth'], 'requestPasswordReset'> };
  requestReset?: (email: string) => Promise<void>;
  confirmReset?: (token: string, password: string) => Promise<void>;
}

export function ForgotPasswordScreen({
  requestReset = requestCustomerPasswordReset,
  confirmReset = confirmCustomerPasswordReset,
}: ForgotPasswordScreenProps) {
  const { errors, setErrors, clear } = useFormErrors();
  const [resetToken] = useState(() => new URLSearchParams(window.location.search).get('token'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [pending, setPending] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [resetComplete, setResetComplete] = useState(false);

  const request = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const emailError = validateEmail(email);
    if (emailError !== undefined) {
      setErrors({ 'forgot-password-email': emailError });
      return;
    }

    setPending(true);
    try {
      await requestReset(email.trim());
    } catch {
      // The confirmation below is deliberately identical if the service declines the request.
    } finally {
      clear();
      setConfirmed(true);
      setPending(false);
    }
  };

  const reset = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};
    const passwordError = validatePassword(password);
    if (passwordError !== undefined) nextErrors['reset-password-new'] = passwordError;
    if (passwordConfirmation.length === 0) {
      nextErrors['reset-password-confirmation'] = 'Confirm your new password.';
    } else if (passwordConfirmation !== password) {
      nextErrors['reset-password-confirmation'] = 'Passwords must match.';
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    if (resetToken === null || resetToken.trim().length === 0) {
      setErrors({ 'reset-password-new': PASSWORD_RESET_FAILURE });
      return;
    }

    setPending(true);
    try {
      await confirmReset(resetToken, password);
      clear();
      setResetComplete(true);
      window.history.replaceState({}, '', customerHref('/reset-password'));
    } catch {
      setErrors({ 'reset-password-new': PASSWORD_RESET_FAILURE });
    } finally {
      setPending(false);
    }
  };

  const resetMode = resetToken !== null;

  return (
    <section
      aria-labelledby="forgot-password-heading"
      style={{ maxWidth: '32rem', margin: '0 auto', padding: space[6] }}
    >
      <h1
        id="forgot-password-heading"
        style={{ ...typeStyle('headingLg'), color: semanticColor.textPrimary, margin: 0 }}
      >
        {resetMode ? 'Choose a new password' : 'Reset your password'}
      </h1>
      <p
        style={{
          ...typeStyle('body'),
          color: semanticColor.textSecondary,
          margin: `${space[2]} 0 ${space[5]}`,
        }}
      >
        {resetMode
          ? 'Enter a new password for your Beauty Burst account.'
          : "Enter your email and we'll provide next steps if an account is available."}
      </p>
      <Card>
        {resetMode ? (
          resetComplete ? (
            <p
              role="status"
              style={{ ...typeStyle('body'), color: semanticColor.textPrimary, margin: 0 }}
            >
              Your password has been updated. Sign in with your new password.
            </p>
          ) : (
            <>
              <ErrorSummary errors={errorsForSummary(errors)} />
              <form noValidate onSubmit={reset} style={{ display: 'grid', gap: space[4] }}>
                <FormField
                  id="reset-password-new"
                  label="New password"
                  hint="Use at least 10 characters, including a letter and a number."
                  error={errors['reset-password-new']}
                  required
                >
                  <input
                    type="password"
                    name="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(event) => {
                      setPassword(event.currentTarget.value);
                      clear('reset-password-new');
                    }}
                  />
                </FormField>
                <FormField
                  id="reset-password-confirmation"
                  label="Confirm new password"
                  error={errors['reset-password-confirmation']}
                  required
                >
                  <input
                    type="password"
                    name="passwordConfirmation"
                    autoComplete="new-password"
                    value={passwordConfirmation}
                    onChange={(event) => {
                      setPasswordConfirmation(event.currentTarget.value);
                      clear('reset-password-confirmation');
                    }}
                  />
                </FormField>
                <Button
                  type="submit"
                  disabled={pending}
                  aria-label={pending ? 'Updating password…' : 'Update password'}
                >
                  {pending ? 'Updating password…' : 'Update password'}
                </Button>
              </form>
            </>
          )
        ) : confirmed ? (
          <p
            role="status"
            style={{ ...typeStyle('body'), color: semanticColor.textPrimary, margin: 0 }}
          >
            {PASSWORD_RESET_CONFIRMATION}
          </p>
        ) : (
          <>
            <ErrorSummary errors={errorsForSummary(errors)} />
            <form noValidate onSubmit={request} style={{ display: 'grid', gap: space[4] }}>
              <FormField
                id="forgot-password-email"
                label="Email address"
                error={errors['forgot-password-email']}
                required
              >
                <input
                  type="email"
                  name="email"
                  autoComplete="email"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.currentTarget.value);
                    clear('forgot-password-email');
                  }}
                />
              </FormField>
              <Button
                type="submit"
                disabled={pending}
                aria-label={pending ? 'Sending instructions…' : 'Send instructions'}
              >
                {pending ? 'Sending instructions…' : 'Send instructions'}
              </Button>
            </form>
          </>
        )}
        <p
          style={{
            ...typeStyle('bodySm'),
            color: semanticColor.textSecondary,
            margin: `${space[4]} 0 0`,
          }}
        >
          <a href={customerHref('/sign-in')} style={{ color: semanticColor.textLink }}>
            Back to sign in
          </a>
        </p>
      </Card>
    </section>
  );
}
